/**
 * Scope switching hook. Changing the active account/team is a server-side,
 * membership-checked mutation (`auth.switchScope`) that re-issues the session
 * token with the new scope — the client never declares scope via request
 * headers. After a switch the React Query cache is fully invalidated so no
 * prior-tenant data is rendered.
 */
import type { TeamWithAccount } from "@nucleus/shared-types";
import { useCallback } from "react";

import { trpc } from "@/lib/trpc";
import { useAuthStore } from "@/stores/auth";

/**
 * Returns an async function that switches the active scope to the given team.
 * Updates the auth store with the re-scoped session and invalidates all cached
 * queries. Throws (FORBIDDEN) if the user is not a member of the target scope.
 */
export function useSwitchScope() {
	const utils = trpc.useUtils();
	const setSession = useAuthStore((s) => s.setSession);
	const setActiveTeam = useAuthStore((s) => s.setActiveTeam);
	const setPermissions = useAuthStore((s) => s.setPermissions);
	const switchScope = trpc.auth.switchScope.useMutation();

	return useCallback(
		async (team: TeamWithAccount) => {
			const result = await switchScope.mutateAsync({
				accountId: team.account.id,
				teamId: team.id,
			});
			setSession(result.session);
			setActiveTeam(team);
			setPermissions(result.session.capabilities);
			// Drop all cached data so components refetch under the new scope
			await utils.invalidate();
		},
		[switchScope, setSession, setActiveTeam, setPermissions, utils]
	);
}
